/**
 * /dashboard/accounting/opening-balance — G_ACC_CR2 frontend.
 *
 * Replaces the prior ComingSoon stub. Composed of:
 *
 *   - AccountingModuleShell (role gate + horizontal module strip)
 *   - OpeningBalanceHistory  (read-only audit ledger of prior imports)
 *   - OpeningBalanceWizard   (3-step: upload → preview → commit)
 *
 * Backend surface (canonical):
 *   POST /api/v1/accounting/opening-balance/preview      — multipart
 *   POST /api/v1/accounting/opening-balance/commit       — step-up TOTP
 *   GET  /api/v1/accounting/opening-balance/imports      — list
 *   GET  /api/v1/accounting/opening-balance/imports/{id} — detail (unused v1)
 *   GET  /api/v1/accounting/opening-balance/template.csv — download
 *
 * RBAC: accountingRole on every endpoint (company_admin / hr_manager /
 * accountant). The role gate is wrapped by AccountingModuleShell — the
 * backend is the security boundary.
 *
 * Statutory anchors:
 *   - Companies Act 2013 §128(1) — true and fair view; opening balances
 *     are the first row of every book of account.
 *   - Companies Act 2013 §128(5) — 8-year retention for the audit
 *     ledger; commit is a write-once event from the UX (period_lock
 *     written in the same TX).
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { OpeningBalanceHistory } from "@/components/accounting/opening-balance-history";
import { OpeningBalanceWizard } from "@/components/accounting/opening-balance-wizard";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/opening-balance",
)({
  component: OpeningBalanceRoute,
});

function OpeningBalanceRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <AccountingModuleShell
      title="Opening balances"
      description="Bulk-import the opening trial balance from your prior accounting system. Write-once, append-only — Companies Act 2013 §128(1)."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <div className="space-y-5">
        <OpeningBalanceHistory />
        <OpeningBalanceWizard />
      </div>
    </AccountingModuleShell>
  );
}
